<script setup lang="ts">
import { useProductSuggestionsStore } from '@/modules/flokee/stores/productSuggestions.store';
import DrawerDialog from '@/components/common/DrawerDialog.vue';
import SmartIcon from '@/components/common/SmartIcon.vue';
import RotatingText from '@/components/common/RotatingText.vue';
import InlineAlert from '@/components/common/InlineAlert.vue';
import { formatNumber } from '@/utils/format.utils';
import Badge from '@/components/common/Badge.vue';
import ScreenLoader from '@/components/common/ScreenLoader.vue';
import CircularProgress from '@/components/common/CircularProgress.vue';
import PrimaryButton from '@/components/common/PrimaryButton.vue';
import { useProductSuggestions } from '@/modules/flokee/composables/useProductSuggestions';

const { t } = useTranslations();

const productSuggestionsStore = useProductSuggestionsStore();

const {
  suggestionsByCategory,
  product,
  image,
  isReady,
  isProcessing,
  isDraft,
  isError,
  currentStep,
  loadingProgress,
  hasTimedOut,
  refetchSuggestions,
} = useProductSuggestions(() => productSuggestionsStore.productId);

const progressMessageByStep = computed(() => ({
  started: t('suggestions.steps.started'),
  screenshot_taken: t('suggestions.steps.screenshot_taken'),
  html_scrapped: t('suggestions.steps.html_scrapped'),
  done: t('suggestions.steps.done'),
  error: t('suggestions.steps.error'),
}));

const currentProgressMessage = computed(() => progressMessageByStep.value[currentStep.value]);

const priceRange = computed(() => {
  const prices = product.value?.payload.variants.map((variant) => parseFloat(variant.price));

  if (!prices) return '';

  const minPrice = formatNumber(Math.min(...prices), {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  const maxPrice = formatNumber(Math.max(...prices), {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

  return minPrice === maxPrice
    ? `${product.value?.currency} ${minPrice}`
    : `${product.value?.currency} ${minPrice} - ${product.value?.currency} ${maxPrice}`;
});

watch(
  [() => productSuggestionsStore.drawerOpen, () => productSuggestionsStore.productId],
  ([drawerOpen, productId]) => {
    if (!drawerOpen || !productId) return;

    void refetchSuggestions();
  },
);
</script>

<template>
  <DrawerDialog v-model="productSuggestionsStore.drawerOpen" size="5xl">
    <template #header>
      <h3 class="flex items-center gap-2 text-lg font-medium">
        <SmartIcon class="hidden !size-5 md:block" />
        {{ t('suggestions.title', { product: product?.title }) }}
      </h3>
    </template>
    <template #default>
      <template v-if="isReady">
        <InlineAlert type="success" class="mb-5 w-full" :message="t('suggestions.success_alert')" />
        <div class="gap-5 md:flex md:flex-row-reverse">
          <div class="md:w-2/5">
            <img
              :src="image"
              alt=""
              class="w-full max-w-full rounded-md ring-1 ring-semantic-border-primary"
            />
            <div class="mt-4 space-y-2">
              <div>
                <h3 class="text-lg font-medium">{{ product?.title }}</h3>
                <a
                  :href="product?.link"
                  :title="product?.link"
                  target="_blank"
                  class="underline-offset-1 hover:underline"
                >
                  <span class="flex items-center gap-1 text-xs text-semantic-text-secondary">
                    #{{ product?.external_id }}
                    <IconifyIcon icon="heroicons:arrow-top-right-on-square-16-solid" />
                  </span>
                </a>
              </div>
              <p class="text-brand-primary">
                {{ priceRange }}
              </p>
              <div v-if="product?.payload?.categories?.length != 0">
                <p class="mb-2">{{ t('suggestions.categories') }}</p>
                <div class="flex flex-row flex-wrap gap-2 text-sm text-semantic-text-secondary">
                  <Badge
                    v-for="category in product?.payload.categories"
                    :key="category.id"
                    color="gray"
                    >{{ category.name.es }}</Badge
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="mt-5 space-y-5 md:mt-0 md:w-3/5">
            <div v-for="(item, key, index) in suggestionsByCategory" :key="key">
              <h3 class="flex items-center gap-2 text-lg font-medium text-brand-primary">
                <span
                  class="flex size-6 items-center justify-center rounded-full border border-brand-primary text-sm text-brand-primary"
                  >{{ index + 1 }}</span
                >
                {{ key }}
              </h3>
              <div class="ml-8 mt-2" v-for="suggestion in item" :key="suggestion.title">
                <div class="flex items-center gap-2">
                  <div class="flex flex-col">
                    <p class="font-medium">{{ suggestion.title }}</p>
                    <p class="text-sm text-semantic-text-secondary">
                      {{ suggestion.suggestion }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else-if="isError">
        <div class="flex flex-col items-center justify-center gap-y-5 py-8 text-center">
          <InlineAlert type="error" class="w-full" :message="t('suggestions.error_alert')" />
          <div class="space-y-2">
            <p class="text-base font-medium text-semantic-text-primary">
              {{ t('suggestions.error_title') }}
            </p>
            <p class="max-w-lg text-sm text-semantic-text-secondary">
              {{ t('suggestions.error_description') }}
            </p>
          </div>
          <PrimaryButton @click="refetchSuggestions">
            {{ t('suggestions.retry') }}
          </PrimaryButton>
        </div>
      </template>
      <template v-else-if="isProcessing || isDraft">
        <div class="flex size-full flex-col items-center justify-center gap-y-5">
          <div class="w-full">
            <InlineAlert type="info" class="w-full" :message="t('suggestions.loading_alert')" />
          </div>
          <div class="flex size-full flex-col items-center justify-center gap-y-5">
            <div class="animation-levitate">
              <img class="h-44 xl:h-60" src="/images/robot/robot-sleep.png" />
            </div>
            <div class="mt-5 flex flex-col items-center justify-center">
              <CircularProgress
                class="!size-[85px] !p-0 !shadow-none"
                :percentage="loadingProgress"
              />
              <RotatingText
                v-if="isDraft"
                class="mt-1 text-lg font-medium text-brand-primary"
                :texts="[t('suggestions.sending')]"
              />
              <p v-else class="mt-1 text-lg font-medium text-brand-primary">
                {{ currentProgressMessage }}
              </p>
              <p
                v-if="hasTimedOut"
                class="mt-2 max-w-md text-center text-sm text-semantic-text-secondary"
              >
                {{ t('suggestions.timeout_message') }}
              </p>
              <PrimaryButton v-if="hasTimedOut" class="mt-4" @click="refetchSuggestions">
                {{ t('suggestions.refresh') }}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <ScreenLoader />
      </template>
    </template>
  </DrawerDialog>
</template>

<style scoped lang="scss">
.animation-levitate {
  animation-name: levitate;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
  animation-duration: 7000ms;
  animation-fill-mode: forwards;
  transform: translateY(15px);
  &.-delayed-1 {
    animation-delay: 2800ms;
  }

  &.-delayed-2 {
    animation-delay: 1700ms;
  }
}

@keyframes levitate {
  50% {
    transform: translateY(-5px);
  }
}
</style>
